import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { API } from "../App";

const QR_ELEMENT_ID = "checkin-qr-reader";

const CheckIn = () => {
  const [staffToken, setStaffToken] = useState(localStorage.getItem("staffToken") || "");
  const [pin, setPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [result, setResult] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [mismatch, setMismatch] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const scannerRef = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${staffToken}` } };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/checkin/login`, { pin });
      localStorage.setItem("staffToken", res.data.token);
      setStaffToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Invalid PIN");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("staffToken");
    setStaffToken("");
  };

  const lookupToken = async (token) => {
    setLookupError("");
    setResult(null);
    setMismatch(null);
    try {
      const res = await axios.get(`${API}/checkin/lookup/${token}`, authHeaders);
      setResult({ ...res.data, token });
    } catch (err) {
      setLookupError(err.response?.data?.detail || "Delegate not found");
    }
  };

  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const html5Qrcode = new Html5Qrcode(QR_ELEMENT_ID);
        scannerRef.current = html5Qrcode;
        await html5Qrcode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          (decodedText) => {
            lookupToken(decodedText);
            stopScanner();
          },
          () => {}
        );
      } catch (err) {
        setLookupError("Camera unavailable — use manual entry below");
        setScanning(false);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (e) {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { if (scannerRef.current) scannerRef.current.stop().catch(() => {}); }, []);

  const handleManualLookup = (e) => {
    e.preventDefault();
    if (manualToken.trim()) lookupToken(manualToken.trim());
  };

  const handleMark = async (day, override = false) => {
    try {
      const res = await axios.post(`${API}/checkin/mark`, { token: result.token, day, override }, authHeaders);
      if (res.data.mismatch) {
        setMismatch({ day, message: res.data.message });
      } else {
        setMismatch(null);
        lookupToken(result.token);
      }
    } catch (err) {
      setLookupError(err.response?.data?.detail || "Check-in failed");
    }
  };

  const loadLogs = async () => {
    const res = await axios.get(`${API}/checkin/logs`, authHeaders);
    setLogs(res.data.data);
    setShowLogs(true);
  };

  if (!staffToken) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <form onSubmit={handleLogin} className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-sm w-full" data-testid="staff-login-form">
          <h1 className="text-xl font-bold text-white mb-4">Staff Check-In</h1>
          {loginError && <div className="bg-red-500/20 border border-red-500 text-red-200 px-3 py-2 rounded mb-3 text-sm" data-testid="staff-login-error">{loginError}</div>}
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Staff PIN"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white mb-4"
            data-testid="staff-pin-input"
          />
          <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg" data-testid="staff-login-btn">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-white">Delegate Check-In</h1>
          <div className="flex gap-2">
            <button onClick={loadLogs} className="text-sky-400 text-sm" data-testid="view-logs-btn">Logs</button>
            <button onClick={handleLogout} className="text-slate-400 text-sm" data-testid="staff-logout-btn">Logout</button>
          </div>
        </div>

        {!scanning ? (
          <button onClick={startScanner} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg mb-4" data-testid="start-scan-btn">
            Scan QR Code
          </button>
        ) : (
          <div className="mb-4">
            <div id={QR_ELEMENT_ID} className="rounded-lg overflow-hidden" data-testid="qr-scanner-view" />
            <button onClick={stopScanner} className="w-full mt-2 bg-slate-700 text-white py-2 rounded-lg" data-testid="stop-scan-btn">Cancel Scan</button>
          </div>
        )}

        <form onSubmit={handleManualLookup} className="flex gap-2 mb-6">
          <input
            type="text"
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            placeholder="Or enter token manually"
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            data-testid="manual-token-input"
          />
          <button type="submit" className="bg-slate-700 text-white px-4 rounded-lg text-sm" data-testid="manual-lookup-btn">Lookup</button>
        </form>

        {lookupError && <div className="bg-red-500/20 border border-red-500 text-red-200 px-3 py-2 rounded mb-4 text-sm" data-testid="lookup-error">{lookupError}</div>}

        {result && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4" data-testid="checkin-result">
            <p className="text-white font-semibold">{result.name}</p>
            <p className="text-slate-400 text-sm">{result.attendeeType} · {result.displayLabel}</p>
            <p className="text-slate-500 text-xs mt-1">Today (London): {result.todayLondon}</p>

            <div className="mt-4 space-y-2">
              {result.entitledDays.map((day) => {
                const isCheckedIn = result.checkedInDays.includes(day);
                return (
                  <div key={day} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-200 text-sm">{day}</span>
                    {isCheckedIn ? (
                      <span className="text-emerald-400 text-xs font-medium" data-testid={`checked-in-${day}`}>✓ Checked In</span>
                    ) : (
                      <button
                        onClick={() => handleMark(day)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded"
                        data-testid={`checkin-btn-${day}`}
                      >
                        Check In
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {mismatch && (
              <div className="mt-4 bg-amber-500/20 border border-amber-500 text-amber-200 px-3 py-3 rounded-lg text-sm" data-testid="day-mismatch-warning">
                <p>{mismatch.message}</p>
                <button
                  onClick={() => handleMark(mismatch.day, true)}
                  className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1.5 rounded"
                  data-testid="override-checkin-btn"
                >
                  Override & Check In Anyway
                </button>
              </div>
            )}
          </div>
        )}

        {showLogs && (
          <div className="mt-6 bg-slate-800 border border-slate-700 rounded-xl p-4" data-testid="checkin-logs">
            <div className="flex justify-between mb-2">
              <h2 className="text-white font-semibold text-sm">Recent Check-Ins</h2>
              <button onClick={() => setShowLogs(false)} className="text-slate-400 text-xs">Close</button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-slate-500 text-xs">No check-ins yet</p>
              ) : logs.map((log) => (
                <div key={log._id} className="text-xs text-slate-300 border-b border-slate-700 py-1">
                  {log.name} — {log.day} {log.staffOverride && <span className="text-amber-400">(override)</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckIn;
